import React from 'react';
import { useIntl } from 'react-intl';

import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { BulkImportStatusType } from '~/enums/BulkImportStatusType';
import { IBulkImportJob } from '~/types/BulkImport';

interface Props {
  status: IBulkImportJob;
  onAcknowledge: () => void;
  onDownloadErrorList: () => void;
  onCancelImportJob: () => void;
}

type StatusMessageProps = React.ComponentProps<typeof StatusMessage>;

export const ImportStatus = ({
  status,
  onAcknowledge,
  onCancelImportJob,
  onDownloadErrorList,
}: Props) => {
  const intl = useIntl();

  const presets: Record<BulkImportStatusType, Preset> = {
    [BulkImportStatusType.New]: {
      getStatus: () => 'info',
      getMessage: () =>
        intl.formatMessage({
          id: 'ImportStatus.usersImportInQueue',
          defaultMessage: 'Users import in queue.',
          description: 'Message shown when import job is in queue',
        }),
    },

    [BulkImportStatusType.Finished]: {
      getStatus: (status: IBulkImportJob) =>
        status.counters.errors ? 'warning' : 'success',
      getMessage: (status: IBulkImportJob) => {
        const parts: string[] = [];

        if (Number.isInteger(status.counters.usersCreated)) {
          parts.push(
            intl.formatMessage(
              {
                id: 'bulkImport.usersCreated',
                defaultMessage:
                  '{count, plural, one {# user successfully created} other {# users successfully created}}.',
                description:
                  'Message for users created during bulk import. Example: count = "5"',
              },
              { count: status.counters.usersCreated },
            ),
          );
        }

        if (Number.isInteger(status.counters.usersUpdated)) {
          parts.push(
            intl.formatMessage(
              {
                id: 'bulkImport.usersUpdated',
                defaultMessage:
                  '{count, plural, one {# user updated} other {# users updated}}.',
                description:
                  'Message for users updated during bulk import. Example: count = "3"',
              },
              { count: status.counters.usersUpdated },
            ),
          );
        }

        if (status.counters.errors) {
          parts.push(
            intl.formatMessage(
              {
                id: 'bulkImport.usersFailed',
                defaultMessage:
                  '{count, plural, one {# user failed to import} other {# users failed to import}}.',
                description:
                  'Message for users that failed to import during bulk import. Example: count = "2"',
              },
              { count: status.counters.errors },
            ),
          );
        }

        return parts.join(' ');
      },
    },

    [BulkImportStatusType.Running]: {
      getStatus: () => 'info',
      getMessage: (status: IBulkImportJob) => {
        return intl.formatMessage(
          {
            id: 'ImportStatus.processingStatus',
            defaultMessage: 'Processing {processed} out of {total}',
            description:
              'Message shown when import job is running. Example: processed = "10", total = "100"',
          },
          {
            processed: status.counters.processed,
            total: status.counters.total,
          },
        );
      },
    },

    [BulkImportStatusType.Failed]: {
      getStatus: () => 'danger',
      getMessage: () =>
        intl.formatMessage({
          id: 'ImportStatus.usersImportFailed',
          defaultMessage: 'Users import failed.',
          description: 'Message shown when import job fails',
        }),
    },

    [BulkImportStatusType.Cancelled]: {
      getStatus: () => 'danger',
      getMessage: () =>
        intl.formatMessage({
          id: 'ImportStatus.userImportCancelled',
          defaultMessage: 'User import job was canceled.',
          description: 'Message shown when import job is canceled',
        }),
    },
  };

  const preset = presets[status.status];
  const message = preset.getMessage(status);
  const statusIcon = preset.getStatus(status);

  const isStillRunning =
    status.status === BulkImportStatusType.New ||
    status.status === BulkImportStatusType.Running;

  return (
    <StatusMessage
      status={statusIcon}
      message={message}
      onDismiss={!isStillRunning ? onAcknowledge : undefined}
      action={
        isStillRunning
          ? {
              text: intl.formatMessage({
                id: 'ImportStatus.cancelImport',
                defaultMessage: 'Cancel Import',
                description: 'Button to cancel import job | maxLength=60',
              }),
              onClick: onCancelImportJob,
            }
          : status.status === BulkImportStatusType.Finished &&
              status.counters.errors
            ? {
                text: intl.formatMessage({
                  id: 'ImportStatus.downloadErrorsList',
                  defaultMessage: 'Download errors list',
                  description: 'Button to download errors list | maxLength=60',
                }),
                onClick: onDownloadErrorList,
              }
            : undefined
      }
    />
  );
};

type Preset = {
  getStatus: (status: IBulkImportJob) => StatusMessageProps['status'];
  getMessage: (status: IBulkImportJob) => string;
};
